import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet, View, Text, TouchableOpacity, Animated, ScrollView, useWindowDimensions } from 'react-native';
import { useActivityStore } from '../store/useActivityStore';
import { TaskDetailModal } from './TaskDetailModal';
import { CompletedTask } from '../store/useActivityStore';
import { useNotificationStore } from '../store/NotificationStore';
import { useStrokeStore } from '../store/useStrokeStore';
import { useAuthStore } from '../store/useAuthStore';
import { useStageTokens } from '../hooks/useStageTokens';
import { useHoldToConfirm } from '../hooks/useHoldToConfirm';
import { AudioPromptButton } from './AudioPromptButton';
import type { KeyStage } from '@eduwrite/shared';

interface LessonCompleteProps {
    onLogout: () => void;
    onSwitchProfile?: () => void;
    onStartPending?: (activityId?: string) => void;
}

const getTaskDisplayTitle = (activity: unknown, index: number): string => {
    const config = (activity as any).config || {};
    switch ((activity as any).type) {
        case 'maths':
            return config.expression || `Math ${index + 1}`;
        case 'spelling':
        case 'common_exception_words':
            return config.word || `Word ${index + 1}`;
        case 'sentence_building':
            return 'Sentence';
        case 'dictation':
            return 'Dictation';
        case 'letter_tracing':
        case 'copy_writing':
        case 'letter_formation':
            return config.targetText || `Writing ${index + 1}`;
        default:
            return config.targetText || (activity as any).title || `Task ${index + 1}`;
    }
}

export const LessonComplete: React.FC<LessonCompleteProps> = ({ onLogout, onSwitchProfile, onStartPending }) => {
    const { width } = useWindowDimensions();
    const isSmall = width < 600;

    const scaleAnim = useRef(new Animated.Value(0.5)).current;
    const opacityAnim = useRef(new Animated.Value(0)).current;
    const sessionHistory = useActivityStore(state => state.sessionHistory);
    const pendingActivities = useActivityStore(state => state.pendingActivities);
    const notifications = useNotificationStore(state => state.notifications);
    const [selectedTask, setSelectedTask] = useState<CompletedTask | null>(null);

    const {
        progress: exitProgress,
        isConfirming: isExitConfirming,
        pressIn: onExitPressIn,
        pressOut: onExitPressOut,
    } = useHoldToConfirm({
        holdMs: 1200,
        onConfirm: onLogout,
    });

    // Voice-first end-of-lesson summary — EYFS pre-readers must hear what
    // they accomplished without needing to read it. KS1/KS2 still get the
    // speaker icon but it is tap-to-play.
    const keyStage = useAuthStore((s) => s.keyStage) as KeyStage | undefined;
    const tokens = useStageTokens(keyStage);
    const completedCount = sessionHistory.length;
    const pendingCount = pendingActivities.length;
    const summaryAudio = pendingCount > 0
        ? `Great job. You finished ${completedCount} ${completedCount === 1 ? 'thing' : 'things'}. You have ${pendingCount} more to go. Tap next task when you are ready.`
        : `All done! You finished ${completedCount} ${completedCount === 1 ? 'thing' : 'things'} today. Well done!`;

    useEffect(() => {
        Animated.parallel([
            Animated.spring(scaleAnim, {
                toValue: 1,
                friction: 5,
                useNativeDriver: true,
            }),
            Animated.timing(opacityAnim, {
                toValue: 1,
                duration: 500,
                useNativeDriver: true,
            })
        ]).start();
    }, [scaleAnim, opacityAnim]);

    return (
        <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            bounces={false}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
        >
            <Animated.View style={[styles.card, isSmall && styles.cardSmall, { opacity: opacityAnim, transform: [{ scale: scaleAnim }] }]}>
                <Text style={[styles.title, isSmall && styles.titleSmall]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Lesson Complete! 🎉</Text>
                <Text style={[styles.heroText, isSmall && styles.heroTextSmall]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Great job! You have finished all your assignments.</Text>
                <View style={styles.audioWrap}>
                    <AudioPromptButton text={summaryAudio} autoPlay={tokens.autoPlayAudio} />
                </View>

                {sessionHistory.length > 0 && (
                    <View style={styles.tasksSection}>
                        <Text style={styles.tasksTitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Completed Tasks ({sessionHistory.length})</Text>
                        <ScrollView style={styles.tasksList} horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tasksListContent}>
                            {sessionHistory.map((item, index) => {
                                const targetText = (item.activity.config as any)?.targetText;
                                const displayTitle = getTaskDisplayTitle(item.activity, index);
                                const unreadCount = targetText
                                    ? notifications.filter(n => !n.read && n.payload?.targetText === targetText).length
                                    : 0;

                                return (
                                    <TouchableOpacity
                                        key={index}
                                        style={styles.taskBadgeWrapper}
                                        onPress={() => setSelectedTask(item)}
                                        accessibilityRole="button"
                                        accessibilityLabel={`View ${displayTitle}`}
                                        accessibilityHint="Double tap to view this completed task"
                                    >
                                        <View style={styles.taskBadge}>
                                            <Text style={styles.taskBadgeText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                                {displayTitle}
                                            </Text>
                                            {item.score !== undefined && (
                                                <View style={styles.scorePill}>
                                                    <Text style={styles.taskBadgeScore} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{item.score}%</Text>
                                                </View>
                                            )}
                                        </View>

                                        {unreadCount > 0 && (
                                            <View style={styles.notificationBadge}>
                                                <Text style={styles.notificationBadgeText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{unreadCount}</Text>
                                            </View>
                                        )}
                                    </TouchableOpacity>
                                );
                            })}
                        </ScrollView>
                    </View>
                )}

                {pendingActivities.length > 0 ? (
                    <TouchableOpacity
                        style={[styles.button, { backgroundColor: '#22c55e', shadowColor: '#22c55e', height: 72 }]}
                        onPress={() => { useStrokeStore.getState().clearStrokes(); onStartPending?.(); }}
                        accessibilityRole="button"
                        accessibilityLabel="Next task"
                        accessibilityHint="Double tap to start the next task"
                    >
                        <Text style={[styles.buttonText, { fontSize: 22 }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>NEXT TASK! ➔</Text>
                        <Text style={[styles.buttonText, { fontSize: 14, opacity: 0.8 }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{pendingActivities.length} more to go</Text>
                    </TouchableOpacity>
                ) : (
                    <View style={{ width: '100%', gap: 12 }}>
                        <View style={styles.waitingBadge}>
                            <View style={styles.waitingDot} />
                            <Text style={styles.waitingBadgeText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>All Caught Up • Waiting for Teacher</Text>
                        </View>
                        {onSwitchProfile && (
                            <TouchableOpacity style={[styles.button, { backgroundColor: '#3B82F6', shadowColor: '#3B82F6' }]} onPress={onSwitchProfile} accessibilityRole="button" accessibilityLabel="Switch Student" accessibilityHint="Double tap to switch to a different student">
                                <Text style={styles.buttonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Switch Student</Text>
                            </TouchableOpacity>
                        )}
                        <TouchableOpacity
                            style={[styles.button, styles.exitButton]}
                            onPressIn={onExitPressIn}
                            onPressOut={onExitPressOut}
                            activeOpacity={0.8}
                            accessibilityRole="button"
                            accessibilityLabel="Exit Class"
                            accessibilityHint="Press and hold to leave the class"
                        >
                            <Animated.View
                                style={[
                                    StyleSheet.absoluteFillObject,
                                    styles.exitProgressFill,
                                    {
                                        width: exitProgress.interpolate({
                                            inputRange: [0, 1],
                                            outputRange: ['0%', '100%'],
                                        }),
                                    },
                                ]}
                            />
                            <Text style={styles.buttonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                {isExitConfirming ? 'Keep Holding to Exit...' : 'Exit Class (Hold)'}
                            </Text>
                        </TouchableOpacity>
                    </View>
                )}
            </Animated.View>

            {selectedTask && (
                <TaskDetailModal
                    task={selectedTask}
                    onClose={() => setSelectedTask(null)}
                />
            )}
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    scrollView: {
        flex: 1,
        width: '100%',
        backgroundColor: '#F8FAFC',
    },
    scrollContent: {
        flexGrow: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 24,
    },
    container: {
        flex: 1,
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F8FAFC',
    },
    card: {
        width: '84%',
        maxWidth: 520,
        padding: 36,
        backgroundColor: '#FFFFFF',
        borderRadius: 24,
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.1,
        shadowRadius: 20,
        elevation: 10,
    },
    cardSmall: {
        width: '94%',
        padding: 20,
        borderRadius: 18,
    },
    title: {
        fontSize: 30,
        fontWeight: 'bold',
        color: '#0F172A',
        marginBottom: 12,
        textAlign: 'center',
    },
    titleSmall: {
        fontSize: 24,
        marginBottom: 8,
    },
    heroText: {
        fontSize: 16,
        color: '#475569',
        textAlign: 'center',
        marginBottom: 12,
    },
    heroTextSmall: {
        fontSize: 14,
        marginBottom: 8,
    },
    audioWrap: {
        marginBottom: 20,
    },
    tasksSection: {
        width: '100%',
        marginBottom: 24,
        alignItems: 'center',
    },
    tasksTitle: {
        fontSize: 15,
        fontWeight: '700',
        color: '#64748B',
        marginBottom: 10,
    },
    tasksList: {
        flexGrow: 0,
        width: '100%',
        paddingTop: 6,
    },
    tasksListContent: {
        paddingHorizontal: 4,
        gap: 8,
        alignItems: 'center',
    },
    taskBadgeWrapper: {
        position: 'relative',
    },
    taskBadge: {
        backgroundColor: '#F8FAFC',
        borderRadius: 14,
        paddingVertical: 10,
        paddingHorizontal: 16,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1.5,
        borderColor: '#E2E8F0',
        minWidth: 90,
    },
    notificationBadge: {
        position: 'absolute',
        top: -6,
        right: -6,
        backgroundColor: '#EF4444',
        borderRadius: 12,
        minWidth: 22,
        height: 22,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 2,
        borderColor: '#FFFFFF',
        paddingHorizontal: 4,
        zIndex: 10,
    },
    notificationBadgeText: {
        color: '#FFFFFF',
        fontSize: 11,
        fontWeight: 'bold',
    },
    taskBadgeText: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#1E293B',
    },
    scorePill: {
        backgroundColor: '#DCFCE7',
        borderRadius: 10,
        paddingHorizontal: 8,
        paddingVertical: 2,
        marginTop: 4,
    },
    taskBadgeScore: {
        fontSize: 12,
        color: '#15803D',
        fontWeight: '800',
    },
    button: {
        width: '100%',
        backgroundColor: '#10B981',
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#10B981',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.3,
        shadowRadius: 10,
        elevation: 6,
        paddingVertical: 14,
        minHeight: 56,
    },
    buttonText: {
        color: '#FFFFFF',
        fontSize: 18,
        fontWeight: 'bold',
    },
    exitButton: {
        backgroundColor: '#64748B',
        shadowColor: '#64748B',
        position: 'relative',
        overflow: 'hidden',
    },
    exitProgressFill: {
        backgroundColor: '#DC2626',
        borderRadius: 16,
    },
    waitingBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F0FDF4',
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: 14,
        borderWidth: 1.5,
        borderColor: '#86EFAC',
        gap: 8,
        marginBottom: 4,
    },
    waitingDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: '#22C55E',
    },
    waitingBadgeText: {
        color: '#15803D',
        fontSize: 13,
        fontWeight: '800',
    },
});
